import { InputLabel, MenuItem, SelectChangeEvent } from '@mui/material';
import { memo, useMemo } from 'react';
import { PmiTooltipError } from 'shell/components/shared/tooltip-error';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Service } from 'shell/models/service';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';

interface RegisterTypeProps {
  disabled?: boolean;
  value: string;
  onChange: (item: Service | Service[], item1?: string) => void;
  error?: boolean;
  errorMessage?: string;
  auth?: boolean;
  allServices?: boolean;
}

const ServiceListComponent = ({
  disabled,
  onChange,
  value,
  error,
  auth,
  allServices,
  errorMessage,
}: RegisterTypeProps): React.ReactElement => {
  /**
   * L'endpoint gestione-cassa-auth2 restituisce i servizi con condizione SERVIZIO.CD_ENTE = codice Ente SERVIZIO.FG_GESTIONE_CASSA = ‘S’
   * L'endpoint gestione-cassa restituisce i servizi andando in JOIN con OPERATORE
   */
  const endpoint = auth ? 'gestione-cassa-auth2' : 'gestione-cassa';
  const [services] = useHttpFetch<HttpResponseList<Service>, { [key: string]: Service }>(
    `${__MANAGEMENT_API_URL__}/servizi/${endpoint}`,
    {
      onBootstrap: true,
      initialData: {},
      options: {
        method: 'GET',
        abortable: true,
        loading: true,
      },
      transformer: (response) => {
        return (response?.data?.payload?.list || []).reduce(
          (obj, entry) => ({
            ...obj,
            [entry.key.codiceServizio]: entry,
          }),
          {}
        );
      },
    }
  );

  const handleChange = (event: SelectChangeEvent<string>): void => {
    if (event.target.value === 'TUTTI') {
      onChange(Object.values(services.data as { [key: string]: Service }), event.target.value);
    } else {
      const service = (services.data as { [key: string]: Service })[event.target.value];
      if (service) {
        onChange(service);
      }
    }
  };

  const menuItems = useMemo(
    () =>
      Object.values((services.data as { [key: string]: Service }) || {})
        ?.sort((a, b) => {
          if (a.descrizione < b.descrizione) {
            return -1;
          }
          return 0;
        })
        .map((service: Service) => (
          <MenuItem
            key={`${service.key.codiceServizio}-service`}
            value={service.key.codiceServizio}
          >
            {service.descrizione}
          </MenuItem>
        )),
    [services.data]
  );

  return (
    <PmiTooltipError show={error} message={errorMessage}>
      <PmiFormControl fullWidth error={error}>
        <InputLabel id="service-label">Servizio</InputLabel>
        <PmiSelect
          labelId="service-label"
          id="service"
          name="service"
          value={value}
          label="Servizio"
          onChange={handleChange}
          disabled={disabled}
        >
          {allServices ? <MenuItem value="TUTTI">Tutti i servizi</MenuItem> : null}
          {menuItems}
        </PmiSelect>
      </PmiFormControl>
    </PmiTooltipError>
  );
};

ServiceListComponent.displayName = 'List Service component';

const ServiceList = memo(ServiceListComponent);

export default ServiceList as typeof ServiceListComponent;
